import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Palmtree, Wind, Droplets, Hammer, Trees, Wrench, HardHat, Sparkles,
  MapPin, ArrowRight, ShieldCheck, Clock, DollarSign, ClipboardList,
} from "lucide-react";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1583468381700-d035630deed6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxmbG9yaWRhJTIwbHV4dXJ5JTIwcHJvcGVydHklMjBob3VzZSUyMGV4dGVyaW9yJTIwcG9vbHxlbnwwfHx8fDE3ODg5NzM2MTJ8MA&ixlib=rb-4.1.0&q=85";

const SERVICES = [
  { icon: Sparkles, name: "Cleaning", d: "Turnover & deep cleans" },
  { icon: Hammer, name: "Handyman", d: "Repairs & punch lists" },
  { icon: Trees, name: "Lawn & Landscaping", d: "Mowing, trim, cleanup" },
  { icon: Droplets, name: "Pool Maintenance", d: "Weekly service & repair" },
  { icon: HardHat, name: "Concrete", d: "Driveways, pads, repair" },
  { icon: Wind, name: "HVAC", d: "AC diagnosis & repair" },
  { icon: Wrench, name: "Plumbing & Electrical", d: "Licensed techs" },
  { icon: ClipboardList, name: "General Contracting", d: "Larger project jobs" },
];

const AREAS = ["Miami", "Fort Lauderdale", "Tampa", "Orlando", "Jacksonville", "St. Petersburg", "Naples", "Sarasota", "Fort Myers", "West Palm Beach"];

const STEPS = [
  { icon: ClipboardList, t: "Submit a work order", d: "Property managers post the job, address, and urgency in seconds." },
  { icon: HardHat, t: "We dispatch a vetted pro", d: "Our team assigns a licensed, insured vendor and sends you one clear quote." },
  { icon: DollarSign, t: "Approve & pay", d: "One transparent all-inclusive price. Approve online and pay securely." },
  { icon: ShieldCheck, t: "Job done, documented", d: "Track status live with before/after photos and a full audit trail." },
];

export default function Marketing() {
  const [lead, setLead] = useState({ name: "", email: "", phone: "", message: "", lead_type: "get_service" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads", lead);
      toast.success("Thanks! Our team will reach out shortly.");
      setLead({ name: "", email: "", phone: "", message: "", lead_type: lead.lead_type });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-[#0B192C] text-white overflow-x-hidden">
      {/* Nav */}
      <header className="glass fixed top-0 inset-x-0 z-50 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-sunburst flex items-center justify-center">
              <Palmtree className="w-5 h-5 text-white" />
            </div>
            <span className="font-display font-bold text-sm sm:text-base">Florida Property Assistants</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/login">
              <Button data-testid="nav-login-button" variant="ghost" className="text-white hover:bg-white/10 hover:text-white">Sign in</Button>
            </Link>
            <a href="#contact">
              <Button data-testid="nav-get-service-cta" className="bg-sunburst hover:bg-orange-600 text-white font-semibold">Get Service</Button>
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center pt-16 grain">
        <img src={HERO} alt="Florida luxury property" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B192C] via-[#0B192C]/85 to-[#0B192C]/30" />
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-8 grid md:grid-cols-12 gap-8 items-center w-full py-20">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="md:col-span-7">
            <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-ocean bg-ocean/10 border border-ocean/30 rounded-full px-3 py-1">
              <MapPin className="w-3.5 h-3.5" /> Serving all of Florida
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-display font-black tracking-tight leading-[1.05]">
              Property services,<br /><span className="text-sunburst">dispatched</span> & done right.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/70 max-w-xl leading-relaxed">
              One point of contact for property managers across the Sunshine State. Submit a work order, we dispatch a vetted vendor, and you get a single transparent price. Cleaners, handymen, lawn, pool, HVAC, concrete & more.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#contact">
                <Button className="bg-sunburst hover:bg-orange-600 text-white font-semibold h-12 px-6 text-base">
                  Get Property Service <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </a>
              <Link to="/register?role=vendor">
                <Button data-testid="nav-become-vendor-cta" variant="outline" className="h-12 px-6 text-base border-white/30 bg-white/5 text-white hover:bg-white hover:text-[#0B192C] font-semibold">
                  Become a Vendor
                </Button>
              </Link>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }} className="md:col-span-5 md:col-start-8">
            <div className="glass rounded-2xl border border-white/15 p-6 space-y-5">
              <p className="font-mono text-xs uppercase tracking-widest text-white/50">Why managers choose us</p>
              {[
                { icon: Clock, t: "Fast dispatch", d: "Emergency & same-week scheduling across FL." },
                { icon: ShieldCheck, t: "Vetted & insured", d: "License, insurance & W-9 verified on every vendor." },
                { icon: DollarSign, t: "One clear price", d: "No haggling with sub-vendors. One invoice, one rate." },
              ].map((f) => (
                <div key={f.t} className="flex gap-3">
                  <div className="w-10 h-10 rounded-lg bg-sunburst/20 flex items-center justify-center shrink-0">
                    <f.icon className="w-5 h-5 text-sunburst" />
                  </div>
                  <div>
                    <p className="font-display font-semibold">{f.t}</p>
                    <p className="text-sm text-white/60">{f.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="bg-[#F8FAFC] text-[#0F172A] py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
          <div className="max-w-2xl">
            <p className="font-mono text-xs uppercase tracking-widest text-ocean">What we handle</p>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight">Every trade your properties need, under one roof.</h2>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICES.map((s, i) => (
              <motion.div key={s.name} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="group bg-white rounded-2xl border border-slate-200 p-6 hover:border-sunburst hover:shadow-xl hover:-translate-y-1 transition-all">
                <div className="w-12 h-12 rounded-xl bg-[#0B192C] flex items-center justify-center group-hover:bg-sunburst transition-colors">
                  <s.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="mt-4 font-display font-semibold text-lg">{s.name}</h3>
                <p className="text-sm text-slate-500 mt-1">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white text-[#0F172A] py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
          <div className="max-w-2xl">
            <p className="font-mono text-xs uppercase tracking-widest text-ocean">How it works</p>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight">From request to resolved in four steps.</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <div key={s.t} className="relative">
                <span className="font-mono text-5xl font-black text-slate-100">{String(i + 1).padStart(2, "0")}</span>
                <div className="w-11 h-11 rounded-xl bg-sunburst/10 flex items-center justify-center -mt-6 relative">
                  <s.icon className="w-5 h-5 text-sunburst" />
                </div>
                <h3 className="mt-4 font-display font-semibold text-lg">{s.t}</h3>
                <p className="text-sm text-slate-500 mt-1 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service areas */}
      <section className="bg-[#0B192C] py-20 lg:py-28 grain relative">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 relative">
          <div className="max-w-2xl">
            <p className="font-mono text-xs uppercase tracking-widest text-ocean">Service areas</p>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight">Coast to coast across Florida.</h2>
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            {AREAS.map((a) => (
              <span key={a} className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm text-white/80 hover:border-sunburst hover:text-white transition-colors">
                <MapPin className="w-3.5 h-3.5 text-sunburst" /> {a}, FL
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Contact / lead */}
      <section id="contact" className="bg-[#F8FAFC] text-[#0F172A] py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-ocean">Get started</p>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight">Tell us what you need.</h2>
            <p className="mt-4 text-slate-600 leading-relaxed max-w-md">
              Whether you manage 5 doors or 500, or you're a vendor who wants steady work — drop your details and our team follows up fast.
            </p>
            <div className="mt-8 flex gap-3">
              <button type="button" onClick={() => setLead((l) => ({ ...l, lead_type: "get_service" }))}
                className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${lead.lead_type === "get_service" ? "bg-[#0B192C] text-white border-[#0B192C]" : "bg-white text-slate-600 border-slate-200"}`}>
                I need service
              </button>
              <button type="button" onClick={() => setLead((l) => ({ ...l, lead_type: "become_vendor" }))}
                className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${lead.lead_type === "become_vendor" ? "bg-[#0B192C] text-white border-[#0B192C]" : "bg-white text-slate-600 border-slate-200"}`}>
                I'm a vendor
              </button>
            </div>
          </div>
          <form data-testid="contact-form" onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 space-y-4 shadow-sm">
            <div>
              <Label>Full name</Label>
              <Input data-testid="lead-name-input" required value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} className="mt-1.5" placeholder="Jane Doe" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input data-testid="lead-email-input" type="email" required value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} className="mt-1.5" placeholder="you@company.com" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} className="mt-1.5" placeholder="(305) 555-0100" />
              </div>
            </div>
            <div>
              <Label>How can we help?</Label>
              <Textarea data-testid="lead-message-input" value={lead.message} onChange={(e) => setLead({ ...lead, message: e.target.value })} className="mt-1.5" rows={4} placeholder="Tell us about your property or services…" />
            </div>
            <Button data-testid="lead-submit-button" type="submit" disabled={sending} className="w-full h-12 bg-sunburst hover:bg-orange-600 text-white font-semibold">
              {sending ? "Sending…" : "Send request"} <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <p className="text-center text-xs text-slate-400">
              Already have an account? <Link to="/login" className="text-ocean font-semibold">Sign in</Link>
            </p>
          </form>
        </div>
      </section>

      <footer className="bg-[#0B192C] border-t border-white/10 py-8">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-white/50">
          <div className="flex items-center gap-2">
            <Palmtree className="w-4 h-4 text-sunburst" />
            <span>Florida Property Assistants</span>
          </div>
          <p>© {new Date().getFullYear()} · Dispatched with care across Florida</p>
        </div>
      </footer>
    </div>
  );
}
